// ============================================================
// equipmentArt.js — the equipment drawings (public/equipment/*.svg) as
// markup that can be placed inside a drawing sheet
//
// The sheet is one SVG document, so each item's drawing is inlined as a
// group (not an <image>): it stays true vector in the PDF and in the
// preview, and can be recoloured / given a print line weight here.
// Call loadEquipmentArt() once (async) before building sheets.
// ============================================================

import { EQ_IMAGES } from '../data/images';

const cache = {};   // catalog id → { vbX, vbY, vbW, vbH, inner }

export async function loadEquipmentArt(ids) {
  const wanted = [...new Set(ids)].filter((id) => EQ_IMAGES[id] && !cache[id]);
  await Promise.all(wanted.map(async (id) => {
    try {
      const text = await (await fetch(EQ_IMAGES[id])).text();
      const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
      const root = doc.documentElement;
      const vb = (root.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
      if (vb.length !== 4 || !vb[2] || !vb[3]) return;
      const ser = new XMLSerializer();
      const inner = [...root.childNodes]
        .filter((n) => n.nodeType === 1 && !['style', 'metadata', 'title', 'desc'].includes(n.nodeName))
        .map((n) => ser.serializeToString(n))
        .join('')
        .replace(/\sxmlns="[^"]*"/g, '');
      cache[id] = { vbX: vb[0], vbY: vb[1], vbW: vb[2], vbH: vb[3], inner };
    } catch (e) {
      console.warn('Equipment drawing failed to load', id, e);   // falls back to an outline
    }
  }));
}

export const hasArt = (id) => !!cache[id];

// Size (points) the drawing actually occupies when fitted into w × h
export function artFit(id, w, h) {
  const a = cache[id];
  if (!a) return { w, h };
  const s = Math.min(w / a.vbW, h / a.vbH);
  return { w: a.vbW * s, h: a.vbH * s };
}

// Markup for one item's drawing, fitted ("meet") into a w × h box (points)
// centred on the origin. Lines get `linePt` weight on paper in `ink`.
export function artMarkup(id, w, h, ink, linePt = 0.6) {
  const a = cache[id];
  if (!a) return '';
  const s = Math.min(w / a.vbW, h / a.vbH);                 // drawing units → pt
  const dx = -(a.vbW * s) / 2 - a.vbX * s;
  const dy = -(a.vbH * s) / 2 - a.vbY * s;
  const lw = (linePt / s).toFixed(4);
  const dash = `${(2 / s).toFixed(3)} ${(1.5 / s).toFixed(3)}`;
  const body = a.inner
    .replace(/#ff9900[0-9a-f]{2}/gi, ink)                     // faded detail lines
    .replace(/#ff9900/gi, ink)
    .replace(/stroke-width="[\d.]+"/g, `stroke-width="${lw}"`)
    .replace(/stroke-dasharray="[^"]*"/g, `stroke-dasharray="${dash}"`)
    .replace(/vector-effect="[^"]*"/g, '');
  return `<g transform="translate(${dx.toFixed(3)},${dy.toFixed(3)}) scale(${s.toFixed(5)})">${body}</g>`;
}
